import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchMapData, fetchElectionResults } from '@/lib/api';
import { getPartyColor } from '@/lib/utils';
import type { Election } from '@/lib/types';

interface DistrictMapProps {
  selectedElection: Election | null;
  onDistrictClick?: (districtId: string) => void;
}

const DistrictMap: React.FC<DistrictMapProps> = ({
  selectedElection,
  onDistrictClick,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const geoJsonLayerRef = useRef<L.GeoJSON | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up existing map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    // Initialize map without tiles - just for coordinate system
    const map = L.map(mapRef.current, {
      center: [28.3949, 84.1240],
      zoom: 7,
      zoomControl: true,
      scrollWheelZoom: true,
      dragging: true,
      attributionControl: false,
    });

    mapInstanceRef.current = map;

    // Load GeoJSON and election data
    Promise.all([
      fetchMapData(),
      selectedElection ? fetchElectionResults(selectedElection.id) : Promise.resolve([])
    ])
      .then(([geoJsonData, electionResults]) => {
        console.log('GeoJSON loaded:', geoJsonData);
        console.log('Election results:', electionResults);

        // Create a map of district winners
        const districtWinners = new Map<string, string>();
        
        if (electionResults && electionResults.length > 0) {
          electionResults.forEach((result: any) => {
            if (result.is_winner) {
              districtWinners.set(result.constituency_code, result.party_code);
            }
          });
        }

        // Remove old layer if exists
        if (geoJsonLayerRef.current) {
          map.removeLayer(geoJsonLayerRef.current);
        }

        // Add GeoJSON layer with proper styling
        const geoJsonLayer = L.geoJSON(geoJsonData, {
          style: (feature) => {
            const districtName = feature?.properties?.DISTRICT || feature?.properties?.name || 'Unknown';
            const winningParty = districtWinners.get(districtName);
            const fillColor = winningParty ? getPartyColor(winningParty) : '#E5E7EB';

            return {
              fillColor: fillColor,
              weight: 2,
              opacity: 1,
              color: '#1F2937',
              fillOpacity: 0.8,
            };
          },
          onEachFeature: (feature: any, layer: any) => {
            const districtName = feature.properties?.DISTRICT || feature.properties?.name || 'Unknown';
            const districtHQ = feature.properties?.HQ || '';
            const province = feature.properties?.PROVINCE || '';
            const winningParty = districtWinners.get(districtName);

            // Hover effects
            layer.on('mouseover', function(this: any) {
              this.setStyle({
                weight: 4,
                color: '#000000',
                fillOpacity: 1,
              });
              
              if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
                this.bringToFront();
              }
            });

            layer.on('mouseout', function(this: any) {
              geoJsonLayer.resetStyle(this);
            });

            // Click handler
            layer.on('click', () => {
              console.log('District clicked:', districtName);
              if (onDistrictClick && feature.properties?.id) {
                onDistrictClick(feature.properties.id);
              }
            });

            // Tooltip
            const tooltipContent = `
              <div style="font-family: system-ui, -apple-system, sans-serif; padding: 4px;">
                <div style="font-size: 16px; font-weight: 600; margin-bottom: 4px;">${districtName}</div>
                ${districtHQ ? `<div style="font-size: 12px; color: #666;">HQ: ${districtHQ}</div>` : ''}
                ${province ? `<div style="font-size: 12px; color: #666;">Province ${province}</div>` : ''}
                ${winningParty ? `<div style="font-size: 12px; margin-top: 4px; color: #000; font-weight: 500;">Winner: ${winningParty}</div>` : ''}
              </div>
            `;
            
            layer.bindTooltip(tooltipContent, {
              permanent: false,
              direction: 'top',
              offset: [0, -10],
              opacity: 0.95,
            });
          },
        }).addTo(map);

        geoJsonLayerRef.current = geoJsonLayer;

        // Fit map to GeoJSON bounds
        const bounds = geoJsonLayer.getBounds();
        if (bounds.isValid()) {
          map.fitBounds(bounds, { padding: [20, 20] });
        }

        setIsLoading(false);
        setError(null);
      })
      .catch((err) => {
        console.error('Error loading map data:', err);
        console.error('Error details:', err.message, err.stack);
        setError(err.message || 'Failed to load map data');
        setIsLoading(false);
      });

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [selectedElection, onDistrictClick]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 font-semibold mb-2">Error Loading Map</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-white">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-[1000]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading Nepal map...</p>
          </div>
        </div>
      )}
      
      <div ref={mapRef} className="w-full h-full" />

      {/* Legend */}
      {!isLoading && (
        <div className="absolute bottom-6 right-6 bg-white p-4 rounded-lg shadow-lg border-2 border-gray-200 z-[1000]">
          <h3 className="text-sm font-bold mb-3 text-gray-800">Party Colors</h3>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#16A34A' }}></div>
              <span className="font-medium">NC</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#DC2626' }}></div>
              <span className="font-medium">UML</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#EF4444' }}></div>
              <span className="font-medium">Maoist</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#F59E0B' }}></div>
              <span className="font-medium">RSP</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#3B82F6' }}></div>
              <span className="font-medium">RPP</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded border border-gray-300" style={{ backgroundColor: '#E5E7EB' }}></div>
              <span className="font-medium">No Data</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500">Hover over districts for details</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistrictMap;
